import React, { useEffect, useState } from "react";
import { ScrollNavbar } from "../../components";
import Footer from "../../components/Footer/Footer";
import Banner from "../../components/Landing/Banner";
import { PhoneIcon, EnvelopeIcon } from "@heroicons/react/24/solid";
import { FaFacebook, FaInstagram, FaTwitter, FaLinkedin } from "react-icons/fa";
import "aos/dist/aos.css";
import AOS from "aos";

const Contact: React.FC = () => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    message: "",
  });

  useEffect(() => {
    AOS.init({ duration: 1000 });
  }, []);

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    console.log("Form submitted:", formData);
    alert("Thanks for reaching out! We'll get back to you soon.");
    setFormData({
      name: "",
      email: "",
      phone: "",
      message: "",
    });
  };

  return (
    <>
      <ScrollNavbar />
      <div
        className="px-4 sm:px-8 lg:px-12"
        style={{ maxWidth: "1400px", margin: "0 auto" }}
      >
        {/* Hero Section */}
        <section className="my-20">
          <div className="text-center">
            <h1
              className="text-4xl md:text-6xl font-mazzardbold mb-6 bg-clip-text text-transparent bg-gradient-to-r from-black to-blue-900"
              data-aos="fade-up"
            >
              Get in Touch
            </h1>
            <p
              className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed"
              data-aos="fade-up"
              data-aos-delay="200"
            >
              Have questions about our services? Need support with a booking?
              We'd love to hear from you.
            </p>
          </div>
        </section>

        {/* Main Contact Section */}
        <section className="my-20">
          <div className="w-full h-full box-border p-8 flex justify-center items-center lg:flex-row flex-col gap-12 relative rounded-3xl">
            <div className="absolute inset-0 bg-gradient-to-br from-gray-50 to-blue-50/20 rounded-3xl z-0"></div>

            {/* Contact Form */}
            <div className="lg:w-3/5 w-full z-10 relative">
              <div
                className="bg-white rounded-2xl shadow-lg p-8"
                data-aos="fade-right"
              >
                <h2 className="text-2xl font-mazzardbold text-gray-900 mb-2">
                  Send us a message
                </h2>
                <p className="text-gray-600 mb-8">
                  We typically respond within a few hours during business hours.
                </p>

                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <label
                        htmlFor="name"
                        className="block text-sm font-medium text-gray-700 mb-2"
                      >
                        Your name
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                        placeholder="John Doe"
                      />
                    </div>
                    <div>
                      <label
                        htmlFor="email"
                        className="block text-sm font-medium text-gray-700 mb-2"
                      >
                        Email address
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                        placeholder="john@example.com"
                      />
                    </div>
                  </div>

                  <div>
                    <label
                      htmlFor="phone"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      Phone number (optional)
                    </label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
                      placeholder="+91 98765 43210"
                    />
                  </div>

                  <div>
                    <label
                      htmlFor="message"
                      className="block text-sm font-medium text-gray-700 mb-2"
                    >
                      What can we help you with?
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      required
                      rows={5}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300 resize-none"
                      placeholder="Tell us about your inquiry, booking questions, or any feedback you have..."
                    />
                  </div>

                  <button
                    type="submit"
                    className="w-full rounded-full py-4 px-8 bg-gradient-to-r from-blue-600 to-blue-700 text-white font-mazzardbold hover:from-blue-700 hover:to-blue-800 transition-all duration-300 transform hover:-translate-y-1 shadow-lg hover:shadow-xl"
                  >
                    Send Message
                  </button>
                </form>
              </div>
            </div>

            {/* Contact Info */}
            <div
              className="lg:w-2/5 w-full space-y-8 z-10 relative"
              data-aos="fade-left"
            >
              {/* Quick Contact */}
              <div className="bg-white rounded-2xl shadow-lg p-6">
                <h3 className="text-xl font-mazzardbold text-gray-900 mb-4">
                  Quick Contact
                </h3>
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                      <PhoneIcon className="h-5 w-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Call us</p>
                      <a
                        href="tel:+918309412828"
                        className="text-gray-900 font-medium hover:text-blue-600 transition-colors"
                      >
                        +91 8309412828
                      </a>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                      <EnvelopeIcon className="h-5 w-5 text-green-600" />
                    </div>
                    <div>
                      <p className="text-sm text-gray-500">Email us</p>
                      <a
                        href="mailto:ride@fusioncab.com"
                        className="text-gray-900 font-medium hover:text-green-600 transition-colors"
                      >
                        ride@fusioncab.com
                      </a>
                    </div>
                  </div>
                </div>
              </div>

              {/* Business Hours */}
              <div className="bg-white rounded-2xl shadow-lg p-6">
                <h3 className="text-xl font-mazzardbold text-gray-900 mb-4">
                  When we're available
                </h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Monday - Friday</span>
                    <span className="text-gray-900">9 AM - 9 PM</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Saturday</span>
                    <span className="text-gray-900">10 AM - 6 PM</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Sunday</span>
                    <span className="text-gray-900">Emergency only</span>
                  </div>
                </div>
              </div>

              {/* Social Links */}
              <div className="bg-white rounded-2xl shadow-lg p-6">
                <h3 className="text-xl font-mazzardbold text-gray-900 mb-4">
                  Follow us
                </h3>
                <p className="text-gray-600 text-sm mb-4">
                  Stay updated with our latest news and offers
                </p>
                <div className="flex gap-3">
                  <a
                    href="https://x.com/FusionCab"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center text-gray-600 hover:text-blue-400 hover:bg-blue-50 transition-all duration-300"
                  >
                    <FaTwitter className="h-4 w-4" />
                  </a>
                  <a
                    href="https://www.instagram.com/fusioncab"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center text-gray-600 hover:text-pink-500 hover:bg-pink-50 transition-all duration-300"
                  >
                    <FaInstagram className="h-4 w-4" />
                  </a>
                  <a
                    href="https://www.facebook.com/info.fusioncab"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center text-gray-600 hover:text-blue-600 hover:bg-blue-50 transition-all duration-300"
                  >
                    <FaFacebook className="h-4 w-4" />
                  </a>
                  <a
                    href="https://www.linkedin.com/company/fusion-cab/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center text-gray-600 hover:text-blue-700 hover:bg-blue-50 transition-all duration-300"
                  >
                    <FaLinkedin className="h-4 w-4" />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Banner Section */}
        <section className="my-20">
          <Banner />
        </section>
      </div>
      <Footer />
    </>
  );
};

export default Contact;
